(ns practitest.frontend.kanban.events
  (:require
    [ajax.core :as ajax]
    [cljs.reader :refer [read-string]]
    [day8.re-frame.http-fx]
    [practitest.frontend.common-old-fe.logging :as log]
    [practitest.frontend.kanban.db :as db]
    [practitest.frontend.events :as events]
    [re-frame.core :as re-frame]))

;; ===========================================================================
;; interceptors

(def common-interceptors
  [re-frame/trim-v])

;; ===========================================================================
;; fx

;; ===========================================================================
;; utils

(def scopes [:kanban :issues :tests :sets :requirements])

(defn parse-edit-entity [location]
  (when-let [[_ project-id module entity-id]
             (re-find #"^https?://[^/]+/p/([\d+])/(requirements|tests|sets|issues)(?:/(\d+).*)?$" location)]
    {:project-id  (read-string project-id)
     :module      module
     :entity-id   (when entity-id (read-string entity-id))
     :entity-type (case module
                    "requirements" "Requirement"
                    "tests"        "QtestDesign"
                    "sets"         "Suite"
                    "issues"       "Issue")}))

(defn current-lanes [lanes entity]
  (->> lanes
       (filter (fn [{:keys [cards]}]
                 (contains? cards [(:entity_type entity) (:id entity)])))
       (map :id)))

(defn entity-path [lanes entity]
  (when-let [lane-id (first (current-lanes lanes entity))]
    [lane-id :cards [(:entity_type entity) (:id entity)]]))

;; ===========================================================================
;; event handlers

(def default-timeout 30000)

(re-frame/reg-event-fx
  :kanban/initialize
  [common-interceptors]
  (fn [{:keys [db]} [options]]
    (log/info :kanban/initialize "Initializing internal structure..." db options)
    (let [merged-db (merge db/default-value db options)]
      {:db         (assoc-in merged-db [:options :collab-custom-events] 
                             {"update" [[:kanban.ws/message]]
                              "online" [[:kanban.ws/message]]
                              "offline" [[:kanban.ws/message]]
                              "presence" [[:kanban.ws/message]]
                              "destroy" [[:kanban.ws/message]]
                              "lane_assigned" [[:kanban.ws/message]]
                              "remove_from_all_lanes" [[:kanban.ws/message]]})
       :dispatch-n [[:kanban/refresh-lanes]
                    [::events/collab-subscribe-scopes scopes]
                    [::events/request-presence [(:current-project-id merged-db)]]]})))

(re-frame/reg-event-fx
  :kanban/refresh-lanes
  [common-interceptors]
  (fn [{:keys [db]} _]
    (log/info :kanban/refresh-lanes)
    {:http-xhrio {:method          :get
                  :uri             (str "/p/" (:current-project-id db) "/kanban/lanes")
                  :timeout         default-timeout
                  :vec-strategy    :rails
                  :format          (ajax/url-request-format :rails)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:kanban/lanes-received]
                  :on-failure      [:kanban/bad-http-result]}}))

(re-frame/reg-event-fx
  :kanban/drop
  [common-interceptors]
  (fn [{:keys [db]} [source-lane-id card-id destination-lane-id position]]
    (log/info :kanban/drop source-lane-id card-id destination-lane-id position)
    (let [source-lane-id       (js/parseInt source-lane-id)
          destination-lane-id  (js/parseInt destination-lane-id)
          [_ entity system-id] (re-find #"^([^:]+):(.*)$" card-id)
          system-id            (js/parseInt system-id)]
      {:db         (update db :kanban/lanes #(db/move-card %1
                                                           {:lane-id source-lane-id
                                                            :card-id card-id}
                                                           {:lane-id  destination-lane-id
                                                            :position (inc position)}))
       :http-xhrio {:method          :post
                    :uri             (str "/p/" (:current-project-id db) "/kanban/change_lane")
                    :params          {:entity-id   system-id
                                      :entity-type entity
                                      :lane-id     destination-lane-id
                                      :position    (inc position)}
                    :timeout         default-timeout
                    :format          (ajax/json-request-format)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [:kanban/lane-changed]
                    :on-failure      [:kanban/bad-http-result]}})))

(re-frame/reg-event-fx
  :kanban/remove-card
  [common-interceptors]
  (fn [{:keys [db]} [lane-id card-id]]
    (log/info :kanban/remove-card lane-id card-id)
    (let [[entity system-id] (db/parse-card-id card-id)]
      {:db         (update-in db [:kanban/lanes lane-id] db/remove-card card-id)
       :http-xhrio {:method          :post
                    :uri             (str "/p/" (:current-project-id db) "/kanban/change_lane")
                    :params          {:entity-id   system-id
                                      :entity-type entity}
                    :timeout         default-timeout
                    :format          (ajax/json-request-format)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [:kanban/lane-changed]
                    :on-failure      [:kanban/bad-http-result]}})))

(re-frame/reg-event-fx
  :kanban/move-lane
  [common-interceptors]
  (fn [{:keys [db]} [lane-id position]]
    (log/info :kanban/move-lane lane-id position)
    {:db         (update db :kanban/lanes #(db/move-lane %1 lane-id (inc position)))
     :http-xhrio {:method          :post
                  :uri             (str "/p/" (:current-project-id db) "/kanban/move_lane")
                  :params          {:lane-id  lane-id
                                    :position (inc position)}
                  :timeout         default-timeout
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:kanban/lane-moved]
                  :on-failure      [:kanban/bad-http-result]}}))

(re-frame/reg-event-fx
  :kanban/add-lane
  [common-interceptors]
  (fn [{:keys [db]} _]
    (log/info :kanban/add-lane)
    {:http-xhrio {:method          :post
                  :uri             (str "/p/" (:current-project-id db) "/kanban/create_lane")
                  :timeout         default-timeout
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:kanban/lanes-received]
                  :on-failure      [:kanban/bad-http-result]}}))

(re-frame/reg-event-fx
  :kanban/delete-lane
  [common-interceptors]
  (fn [{:keys [db]} [lane-id]]
    (log/info :kanban/delete-lane lane-id)
    {:db         (update db :kanban/lanes dissoc lane-id)
     :http-xhrio {:method          :post
                  :uri             (str "/p/" (:current-project-id db) "/kanban/delete_lane")
                  :params          {:lane-id lane-id}
                  :timeout         default-timeout
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:kanban/lane-removed]
                  :on-failure      [:kanban/bad-http-result]}}))

(re-frame/reg-event-db
  :kanban/lane-removed
  [common-interceptors]
  (fn [db [result]]
    (log/info :kanban/lane-removed result)
    db))

(re-frame/reg-event-fx
  :kanban/change-lane-name
  [common-interceptors]
  (fn [{:keys [db]} [lane-id name]]
    (log/info :kanban/change-lane-name lane-id name)
    {:db         (assoc-in db [:kanban/lanes lane-id :name] name)
     :http-xhrio {:method          :post
                  :uri             (str "/p/" (:current-project-id db) "/kanban/rename_lane")
                  :params          {:lane-id lane-id
                                    :name    name}
                  :timeout         default-timeout
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:kanban/lane-renamed]
                  :on-failure      [:kanban/bad-http-result]}}))

(re-frame/reg-event-fx
  :kanban/refresh-lane
  [common-interceptors]
  (fn [{:keys [db]} [lane-id]]
    (log/info :kanban/refresh-lane lane-id)
    {:db         db
     :http-xhrio {:method          :get
                  :uri             (str "/p/" (:current-project-id db) "/kanban/lane")
                  :params          {:lane-id lane-id}
                  :timeout         default-timeout
                  :vec-strategy    :rails
                  :format          (ajax/url-request-format :rails)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:kanban/lane-received]
                  :on-failure      [:kanban/bad-http-result]}}))

(re-frame/reg-event-fx
  :kanban/run-testset
  [common-interceptors]
  (fn [_ [testset]]
    (log/info :kanban/run-testset)
    {:http-xhrio {:method          :post
                  :uri             (:run_uri testset)
                  :timeout         default-timeout
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:kanban/testset-run]
                  :on-failure      [:kanban/bad-http-result]}}))

(re-frame/reg-event-db
  :kanban/testset-run
  [common-interceptors]
  (fn [db [result]]
    (log/info :kanban/testset-run result)
    (set! (.-location js/window) (:redirect result))
    db))

(re-frame/reg-event-fx
  :kanban/lanes-received
  [common-interceptors]
  (fn [{:keys [db]} [lanes]]
    (log/info :kanban/lanes-received lanes)
    {:db         (assoc db :kanban/lanes (->> lanes
                                              (map (fn [{:keys [id] :as lane}]
                                                     [id lane]))
                                              (into {})))
     :dispatch-n (map (fn [lane]
                        [:kanban/refresh-lane (:id lane)])
                      lanes)}))

(re-frame/reg-event-db
  :kanban/lane-moved
  [common-interceptors]
  (fn [db [lanes]]
    (log/info :kanban/lane-moved lanes)
    db))

(re-frame/reg-event-db
  :kanban/lane-renamed
  [common-interceptors]
  (fn [db [lane]]
    (log/info :kanban/lane-renamed lane)
    db))

(re-frame/reg-event-db
  :kanban/lane-received
  [common-interceptors]
  (fn [db [lane]]
    (log/info :kanban/lane-received lane)
    (-> db
        (assoc-in [:kanban/lanes (:id lane) :name] (:name lane))
        (assoc-in [:kanban/lanes (:id lane) :cards]
                  (->> (:entities lane)
                       (map (fn [entity]
                              [[(:entity entity) (:system_id entity)] entity]))
                       (into {}))))))

(re-frame/reg-event-db
  :kanban/lane-changed
  [common-interceptors]
  (fn [db [data]]
    (log/info :kanban/lane-changed data)
    db))

(re-frame/reg-event-fx
  :kanban/cleanup
  [common-interceptors]
  (fn [_ _]
    (log/info :kanban/cleanup "Cleaning up kanban subscriptions")
    {:dispatch [::events/collab-unsubscribe-scopes scopes]}))

(re-frame/reg-event-fx
  :kanban.ws/message
  [common-interceptors]
  (fn [{:keys [db]} [msg]]
    (log/info :kanban.ws/message msg)
    (let [[db dispatch] (case (or (:res msg) (:action msg))
                          "online"
                          [(let [user        (merge (:user msg) (:args msg))
                                 edit-entity (parse-edit-entity (:location user))
                                 entity      (when (and (:entity-id edit-entity)
                                                        (:entity-type edit-entity))
                                               {:entity (:entity-type edit-entity)
                                                :id     (:entity-id edit-entity)})]
                             (cond-> (update-in db [:kanban/online-users (:id user)]
                                                (fn [state]
                                                  (if state
                                                    (update state :location
                                                            conj (:location user))
                                                    (update user :location
                                                            (fn [location]
                                                              (set [location]))))))

                                     (not= (:current-user-id db) (:id user))
                                     (db/append-action-log
                                       (str "User " (:name user)
                                            " is online(" (:state user) ")"))

                                     entity
                                     (assoc-in [:kanban/online-users (:id user) :editing]
                                               [(:entity entity) (:id entity)])))
                           nil]

                          "offline"
                          [(cond-> (update db :kanban/online-users
                                           dissoc (get-in msg [:user :id]))
                                   (not= (:current-user-id db) (get-in msg [:user :id]))
                                   (db/append-action-log
                                     (str "User " (get-in msg [:user :name])
                                          " is offline")))
                           nil]

                          "presence"
                          [(let [online-users (->> (vals (:projects msg))
                                                   (apply concat)
                                                   distinct
                                                   (map (fn [{:keys [id] :as user}]
                                                          [id
                                                           (update user :location
                                                                   (fn [location]
                                                                     (set [location])))]))
                                                   (into {}))]
                             (assoc db :kanban/online-users online-users))
                           nil]

                          "subscribe"
                          [db nil]

                          "update"
                          (if-let [path (entity-path (vals (:kanban/lanes db)) msg)]
                            [(db/append-action-log
                               db
                               (str "User " (get-in msg [:user :name])
                                    " updated " (:entity msg) " " (:display_id msg)))
                             [[:kanban/refresh-lane (first path)]]]
                            [db nil])

                          "destroy"
                          [(-> db
                               (db/remove-entity-from-all-lanes [(:entity_type msg) (:id msg)])
                               (db/append-action-log (str "User " (get-in msg [:user :name])
                                                          " destroyed " (:entity msg) " " (:display_id msg))))
                           nil]

                          "lane_assigned"
                          (let [lanes-to-refresh (current-lanes (vals (:kanban/lanes db))
                                                                msg)]
                            [(db/append-action-log
                               db
                               (str "User " (get-in msg [:user :name])
                                    " assigned " (:entity msg) " " (:display_id msg)
                                    " to lane " (:lane_name msg)))
                             (map #(vector :kanban/refresh-lane %)
                                  (conj lanes-to-refresh (:lane msg)))])

                          "remove_from_all_lanes"
                          (let [lanes-to-refresh (current-lanes (vals (:kanban/lanes db))
                                                                msg)]
                            [(db/append-action-log
                               db
                               (str "User " (get-in msg [:user :name])
                                    " removed " (:entity msg) " " (:display_id msg)
                                    " from lane " (get-in db [:kanban/lanes
                                                              (first lanes-to-refresh)
                                                              :name])))
                             (map #(vector :kanban/refresh-lane %) lanes-to-refresh)])

                          (do
                            (log/info :kanban.ws/message "unsupported" msg)
                            [db nil]))]
      (cond-> {:db db}
              dispatch (assoc :dispatch-n dispatch)))))

(re-frame/reg-event-db
  :kanban/bad-http-result
  [common-interceptors]
  (fn [db [error]]
    (log/info :kanban/bad-http-result error)
    db))

(re-frame/reg-event-db
  :kanban/switch-mode
  [common-interceptors]
  (fn [db [mode]]
    (log/info :kanban/switch-mode mode)
    (assoc db :mode mode)))

(re-frame/reg-event-db
  :kanban/set-card-types-filter
  [common-interceptors]
  (fn [db [filter]]
    (log/info :kanban/set-card-types-filter filter)
    (assoc db :card-types-filter filter)))

(re-frame/reg-event-db
  :kanban/set-assigned-to-filter
  [common-interceptors]
  (fn [db [filter]]
    (log/info :kanban/set-assigned-to-filter filter)
    (assoc db :assigned-to-filter filter)))

(re-frame/reg-event-db
  :kanban/toggle-user-list
  [common-interceptors]
  (fn [db _]
    (log/info :kanban/toggle-user-list)
    (update-in db [:user-list :show?] not)))

(re-frame/reg-event-db
  :kanban/toggle-action-list
  [common-interceptors]
  (fn [db _]
    (log/info :kanban/toggle-action-list)
    (update-in db [:action-log :show?] not)))